import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/toaster';

// Import console helpers for global diagnostic functions
import '@/utils/ordensServico/consoleHelpers';

import Layout from '@/components/layout/Layout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import Login from '@/pages/Login';

const Orcamentos = lazy(() => import('@/pages/Orcamentos'));
const OrdensServico = lazy(() => import('@/pages/OrdensServico'));
const Chamados = lazy(() => import('@/pages/Chamados'));
const ChamadosAnaliseGeral = lazy(() => import('@/pages/ChamadosAnaliseGeral'));
const ChamadosProjecoes = lazy(() => import('@/pages/ChamadosProjecoes'));
const ChamadosProjecoesRelatorio = lazy(() => import('@/pages/ChamadosProjecoesRelatorio'));
const VolumeHoras = lazy(() => import('@/pages/VolumeHoras'));
const RelatorioResultadosOperador = lazy(() => import('@/pages/RelatorioResultadosOperador'));
const RelatorioAnaliseMensalOs = lazy(() => import('@/pages/RelatorioAnaliseMensalOs'));
const Clientes = lazy(() => import('@/pages/Clientes'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Funcionarios = lazy(() => import('@/pages/Funcionarios'));
const Analises = lazy(() => import('@/pages/Analises'));
const AnaliseResumo = lazy(() => import('@/pages/AnaliseResumo'));
const Geral = lazy(() => import('@/pages/Geral'));
const AnalisePorHora = lazy(() => import('@/pages/AnalisePorHora'));
const Consolidado = lazy(() => import('@/pages/Consolidado'));
const FolhaPagamento = lazy(() => import('@/pages/FolhaPagamento'));
const RentabilidadeProjetos = lazy(() => import('@/pages/RentabilidadeProjetos'));
const PaginaInicial = lazy(() => import('@/components/auth/PaginaInicial'));
const LayoutPage = lazy(() => import('@/pages/LayoutPage'));
const LayoutModernizacaoPiloto = lazy(() => import('@/pages/LayoutModernizacaoPiloto'));
const LayoutModernizacaoOrcamentosPiloto = lazy(() => import('@/pages/LayoutModernizacaoOrcamentosPiloto'));
const Usuarios = lazy(() => import('@/pages/Usuarios'));
const CategoriaFinanceira = lazy(() => import('@/pages/CategoriaFinanceira'));
const ContasReceber = lazy(() => import('@/pages/ContasReceber'));
const ContasPagar = lazy(() => import('@/pages/ContasPagar'));
const MovimentoBancarioPage = lazy(() => import('@/pages/MovimentoBancario'));
const DRE = lazy(() => import('@/pages/DRE'));
const DREPrevisto = lazy(() => import('@/pages/DREPrevisto'));
const FluxoCaixa = lazy(() => import('@/pages/FluxoCaixa'));
const Faturamento = lazy(() => import('@/pages/Faturamento'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const AnaliseFinanceira = lazy(() => import('./pages/AnaliseFinanceira'));
const DepartamentoPessoalKanban = lazy(() => import('./pages/DepartamentoPessoalKanban'));
const OperacaoToDoKanban = lazy(() => import('./pages/OperacaoToDoKanban'));
const OperacaoFechamento = lazy(() => import('./pages/OperacaoFechamento'));
const FinanceiroToDoKanban = lazy(() => import('./pages/FinanceiroToDoKanban'));
const RhFuncoes = lazy(() => import('./pages/rh/RhFuncoes'));
const RhVagas = lazy(() => import('./pages/rh/RhVagas'));
const RhCandidatos = lazy(() => import('./pages/rh/RhCandidatos'));
const RhCandidaturas = lazy(() => import('./pages/rh/RhCandidaturas'));
const RhEntrevistas = lazy(() => import('./pages/rh/RhEntrevistas'));
const RhPareceres = lazy(() => import('./pages/rh/RhPareceres'));
const RhPipeline = lazy(() => import('./pages/rh/RhPipeline'));
const TributosNcmIpi = lazy(() => import('./pages/TributosNcmIpi'));
const TributosIcmsRegrasUf = lazy(() => import('./pages/TributosIcmsRegrasUf'));
const TributosTokensApi = lazy(() => import('./pages/TributosTokensApi'));
const TributosPerfilFiscal = lazy(() => import('./pages/TributosPerfilFiscal'));
const DiagnosticoProtheus = lazy(() => import('./pages/tributacao/DiagnosticoProtheus'));
const TributacaoEstabelecimentos = lazy(() => import('./pages/tributacao/sped/TributacaoEstabelecimentos'));
const TributacaoSpedValidacao = lazy(() => import('./pages/tributacao/sped/TributacaoSpedValidacao'));
const TributacaoProdutos = lazy(() => import('./pages/TributacaoProdutos'));
const TributacaoPerfilParticipante = lazy(() => import('./pages/TributacaoPerfilParticipante'));
const TributacaoPerfilOrigemDestino = lazy(() => import('./pages/TributacaoPerfilOrigemDestino'));
const TributacaoTipoOperacao = lazy(() => import('./pages/TributacaoTipoOperacao'));
const TributacaoRegraBaseCalculo = lazy(() => import('./pages/TributacaoRegraBaseCalculo'));
const TributacaoRegraAliquota = lazy(() => import('./pages/TributacaoRegraAliquota'));
const TributacaoRegraDocumentoFiscal = lazy(() => import('./pages/TributacaoRegraDocumentoFiscal'));
const TributacaoRegraTitulo = lazy(() => import('./pages/TributacaoRegraTitulo'));
const TributacaoRegraCalculoFinanceiro = lazy(() => import('./pages/TributacaoRegraCalculoFinanceiro'));
const TributacaoRegraVencimento = lazy(() => import('./pages/TributacaoRegraVencimento'));
const TributacaoRegraRetencao = lazy(() => import('./pages/TributacaoRegraRetencao'));
const TributacaoRegraFinanceira = lazy(() => import('./pages/TributacaoRegraFinanceira'));
const TributacaoRegraEscrituracao = lazy(() => import('./pages/TributacaoRegraEscrituracao'));
const TributacaoCodigoPrestacaoServico = lazy(() => import('./pages/TributacaoCodigoPrestacaoServico'));
const TributacaoCadastroSt = lazy(() => import('./pages/TributacaoCadastroSt'));
const TributacaoCadastroFecpIcms = lazy(() => import('./pages/TributacaoCadastroSt').then((m) => ({ default: m.TributacaoCadastroFecpIcms })));
const TributacaoCadastroFcpSt = lazy(() => import('./pages/TributacaoCadastroSt').then((m) => ({ default: m.TributacaoCadastroFcpSt })));
const TributacaoExTarifario = lazy(() => import('./pages/TributacaoExTarifario'));
const TributacaoRegraCusto = lazy(() => import('./pages/TributacaoRegraCusto'));
const TributacaoFinancialRulesPage = lazy(() => import('./pages/TributacaoFinancialRulesPage'));
const TributacaoConfronto = lazy(() => import('./pages/tributacao/confronto/TributacaoConfronto'));
const PainelFiscal = lazy(() => import('./pages/tributacao/PainelFiscal'));
const PassoAPasso = lazy(() => import('./pages/tributacao/PassoAPasso'));
const TributacaoKbFontes = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbFontes'));
const TributacaoKbNovidades = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbNovidades'));
const TributacaoKbCclassTrib = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCclassTrib'));
const TributacaoKbLocalidades = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbLocalidades'));
const TributacaoKbCfop = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCfop'));
const TributacaoKbNatureza = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbNatureza'));
const TributacaoKbCst = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCst'));
const TributacaoKbNcm = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbNcm'));
const TributacaoKbDominios = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbDominios'));
const TributacaoKbTributos = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbTributos'));
const TributacaoKbRegras = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbRegras'));
const TributacaoKbAliquotas = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbAliquotas'));
const TributacaoKbFcp = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbFcp'));
const TributacaoKbIcmsSt = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbIcmsSt'));
const TributacaoKbCest = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCest'));
const TributacaoKbCnae = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCnae'));
const TributacaoKbTipi = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbTipi'));
const TributacaoKbCruzamentoNcm = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCruzamentoNcm'));
const TributacaoKbExTarifario = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbExTarifario'));
const TributacaoKbAnp = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbAnp'));
const TributacaoKbUtrib = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbUtrib'));
const TributacaoKbPtax = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbPtax'));
const TributacaoKbNbs = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbNbs'));
const TributacaoKbServicosNfse = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbServicosNfse'));
const TributacaoKbCindOp = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCindOp'));
const TributacaoKbSped = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbSped'));
const TributacaoKbUfBeneficios = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbUfBeneficios'));
const TributacaoKbRegimesEspeciais = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbRegimesEspeciais'));
const TributacaoKbNormas = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbNormas'));
const TributacaoKbConvenios = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbConvenios'));
const TributacaoKbSimples = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbSimples'));
const TributacaoKbCbenef = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbCbenef'));
const TributacaoKbIssMunicipio = lazy(() => import('./pages/tributacao/base-conhecimento/TributacaoKbIssMunicipio'));
const ContaAzulAuth = lazy(() => import('./pages/ContaAzulAuth'));
const ContaAzulCallback = lazy(() => import('./pages/ContaAzulCallback'));
const TestCallback = lazy(() => import('./pages/TestCallback'));
const AgendamentoLista = lazy(() => import('./pages/layout/AgendamentoLista'));
const AgendamentoRotinaDetalhe = lazy(() => import('./pages/layout/AgendamentoRotinaDetalhe'));
const CrmDashboard = lazy(() => import('./pages/crm/CrmDashboard'));
const CrmLeads = lazy(() => import('./pages/crm/CrmLeads'));
const CrmEmpresas = lazy(() => import('./pages/crm/CrmEmpresas'));
const CrmPipeline = lazy(() => import('./pages/crm/CrmPipeline'));
const CrmPipelineV2 = lazy(() => import('./pages/crm/CrmPipelineV2'));
const CrmReunioes = lazy(() => import('./pages/crm/CrmReunioes'));
const CrmProdutos = lazy(() => import('./pages/crm/CrmProdutos'));
const CrmOrigens = lazy(() => import('./pages/crm/CrmOrigens'));
const CrmAiPrompts = lazy(() => import('./pages/crm/CrmAiPrompts'));
import { TaskAgendaRunner } from '@/components/taskAgenda/TaskAgendaRunner';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes (aumentado de 5 para 10)
      gcTime: 15 * 60 * 1000, // 15 minutes (aumentado de 10 para 15)
      retry: 1, // Reduzir tentativas de retry
      retryDelay: 1000, // Delay entre tentativas
      refetchOnWindowFocus: false, // Desabilitar refetch automático
      refetchOnMount: false, // Desabilitar refetch no mount
      refetchOnReconnect: false, // Desabilitar refetch na reconexão
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

const PageFallback = () => (
  <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
    Carregando rotina...
  </div>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <TaskAgendaRunner />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* Test route para verificar se rotas funcionam */}
            <Route path="/test-callback" element={<TestCallback />} />
            {/* Callback route - usando rota sem /api/ para evitar conflitos */}
            <Route path="/auth/contaazul/callback" element={<ContaAzulCallback />} />
            <Route path="/api/contaazul/callback" element={<ContaAzulCallback />} />
            {/* Fallback para qualquer variação */}
            <Route path="/contaazul/callback" element={<ContaAzulCallback />} />
            <Route path="/conta-azul/callback" element={<ContaAzulCallback />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<PaginaInicial />} />
              <Route path="orcamentos" element={
                <ProtectedRoute requiredPermission="Orçamentos">
                  <Orcamentos />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm" element={<Navigate to="/comercial/crm/dashboard" replace />} />
              <Route path="comercial/crm/dashboard" element={
                <ProtectedRoute requiredPermission="CRM - Dashboard">
                  <CrmDashboard />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/leads" element={
                <ProtectedRoute requiredPermission="CRM - Leads">
                  <CrmLeads />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/empresas" element={
                <ProtectedRoute requiredPermission="CRM - Empresas">
                  <CrmEmpresas />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/funil" element={
                <ProtectedRoute requiredPermission="CRM - Funil">
                  <CrmPipelineV2 />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/funil-v2" element={
                <ProtectedRoute requiredPermission="CRM - Funil">
                  <CrmPipelineV2 />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/funil-legado" element={
                <ProtectedRoute requiredPermission="CRM - Funil">
                  <CrmPipeline />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/reunioes" element={
                <ProtectedRoute requiredPermission="CRM - Reuniões">
                  <CrmReunioes />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/produtos" element={
                <ProtectedRoute requiredPermission="CRM - Produtos">
                  <CrmProdutos />
                </ProtectedRoute>
              } />
              <Route path="comercial/crm/origens" element={
                <ProtectedRoute requiredPermission="CRM - Origens">
                  <CrmOrigens />
                </ProtectedRoute>
              } />
              <Route path="configuracoes/ia/prompts" element={
                <ProtectedRoute requiredPermission="IA - Prompts">
                  <CrmAiPrompts />
                </ProtectedRoute>
              } />
              <Route path="ordens-servico" element={
                <ProtectedRoute requiredPermission="Ordens de Serviço">
                  <OrdensServico />
                </ProtectedRoute>
              } />
              <Route path="operacao/agenda-diversos" element={<Navigate to="/layout/agendamento" replace />} />
              <Route path="agenda-diversos" element={<Navigate to="/layout/agendamento" replace />} />
              <Route path="layout/agendamento" element={
                <ProtectedRoute requiredPermission={['Agendamento', 'Agenda Diversos', 'Layout']}>
                  <AgendamentoLista />
                </ProtectedRoute>
              } />
              <Route path="layout/agendamento/:tipo" element={
                <ProtectedRoute requiredPermission={['Agendamento', 'Agenda Diversos', 'Layout']}>
                  <AgendamentoRotinaDetalhe />
                </ProtectedRoute>
              } />
              <Route path="layout/piloto-modernizacao" element={
                <ProtectedRoute requiredPermission={['Layout', 'Dashboard', 'Relatórios - Análise Mensal OS']}>
                  <LayoutModernizacaoPiloto />
                </ProtectedRoute>
              } />
              <Route path="layout/piloto-modernizacao-orcamentos" element={
                <ProtectedRoute requiredPermission={['Layout', 'Dashboard', 'Orçamentos']}>
                  <LayoutModernizacaoOrcamentosPiloto />
                </ProtectedRoute>
              } />
              <Route path="chamados" element={
                <ProtectedRoute requiredPermission="Chamados">
                  <Chamados />
                </ProtectedRoute>
              } />
              <Route path="chamados/analise-geral" element={
                <ProtectedRoute requiredPermission="Chamados">
                  <Suspense fallback={
                    <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
                      Carregando análise…
                    </div>
                  }>
                    <ChamadosAnaliseGeral />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="chamados/projecoes" element={
                <ProtectedRoute requiredPermission="Chamados">
                  <ChamadosProjecoes />
                </ProtectedRoute>
              } />
              <Route path="relatorios/analise-projetos" element={
                <ProtectedRoute requiredPermission="Relatórios - Análise de Projetos">
                  <ChamadosProjecoesRelatorio />
                </ProtectedRoute>
              } />
              <Route path="relatorios/volume-horas" element={
                <ProtectedRoute requiredPermission="Relatórios - Volume de Horas">
                  <VolumeHoras />
                </ProtectedRoute>
              } />
              <Route
                path="relatorios/resultados-operador"
                element={
                  <ProtectedRoute requiredPermission="Relatórios - Resultados por Operador">
                    <Suspense
                      fallback={
                        <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
                          Carregando relatório…
                        </div>
                      }
                    >
                      <RelatorioResultadosOperador />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="relatorios/analise-mensal-os"
                element={
                  <ProtectedRoute requiredPermission="Relatórios - Análise Mensal OS">
                    <Suspense
                      fallback={
                        <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
                          Carregando relatório…
                        </div>
                      }
                    >
                      <RelatorioAnaliseMensalOs />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route path="clientes" element={
                <ProtectedRoute requiredPermission="Clientes">
                  <Clientes />
                </ProtectedRoute>
              } />
              <Route path="funcionarios" element={
                <ProtectedRoute requiredPermission="Funcionários">
                  <Funcionarios />
                </ProtectedRoute>
              } />
              <Route path="usuarios" element={
                <ProtectedRoute requiredPermission="Usuários">
                  <Usuarios />
                </ProtectedRoute>
              } />
              <Route path="financeiro/conta-azul-auth" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <ContaAzulAuth />
                </ProtectedRoute>
              } />
              <Route path="financeiro/cadastro/categoria" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <CategoriaFinanceira />
                </ProtectedRoute>
              } />
              <Route path="financeiro/contas-receber" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <ContasReceber />
                </ProtectedRoute>
              } />
              <Route path="financeiro/contas-pagar" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <ContasPagar />
                </ProtectedRoute>
              } />
              <Route path="financeiro/movimento-bancario" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <MovimentoBancarioPage />
                </ProtectedRoute>
              } />
              <Route path="financeiro/dre" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <DRE />
                </ProtectedRoute>
              } />
              <Route path="financeiro/dre-previsto" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <DREPrevisto />
                </ProtectedRoute>
              } />
              <Route path="financeiro/fluxo-caixa" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <FluxoCaixa />
                </ProtectedRoute>
              } />
              <Route path="financeiro/faturamento" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <Faturamento />
                </ProtectedRoute>
              } />
              <Route path="financeiro/analise-financeira" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <Suspense fallback={
                    <div className="flex min-h-[40vh] items-center justify-center text-sm text-muted-foreground">
                      Carregando análise financeira...
                    </div>
                  }>
                    <AnaliseFinanceira />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="financeiro/dp-contratos" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <DepartamentoPessoalKanban />
                </ProtectedRoute>
              } />
              <Route path="financeiro/to-do" element={
                <ProtectedRoute requiredPermission="Financeiro">
                  <FinanceiroToDoKanban />
                </ProtectedRoute>
              } />
              <Route path="financeiro/tributos-ncm-icms" element={<Navigate to="/tributacao/icms-uf" replace />} />
              <Route path="tributacao/ncm-ipi" element={
                <ProtectedRoute requiredPermission={['Tributação - NCM ICMS']}>
                  <TributosNcmIpi />
                </ProtectedRoute>
              } />
              <Route path="tributacao/icms-uf" element={
                <ProtectedRoute requiredPermission={['Tributação - NCM ICMS']}>
                  <TributosIcmsRegrasUf />
                </ProtectedRoute>
              } />
              <Route path="tributacao/ncm-icms" element={
                <Navigate to="/tributacao/icms-uf" replace />
              } />
              <Route path="tributacao/tokens-api" element={
                <ProtectedRoute requiredPermission={['Tributação - Tokens API']}>
                  <TributosTokensApi />
                </ProtectedRoute>
              } />
              <Route path="tributacao/perfil-fiscal" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfil Fiscal']}>
                  <TributosPerfilFiscal />
                </ProtectedRoute>
              } />
              <Route path="tributacao/diagnostico-protheus" element={
                <ProtectedRoute requiredPermission={['Tributação - Diagnóstico Protheus']}>
                  <DiagnosticoProtheus />
                </ProtectedRoute>
              } />
              <Route path="tributacao/sped/estabelecimentos" element={
                <ProtectedRoute requiredPermission={['Tributação - SPED']}>
                  <TributacaoEstabelecimentos />
                </ProtectedRoute>
              } />
              <Route path="tributacao/sped/validacao" element={
                <ProtectedRoute requiredPermission={['Tributação - SPED']}>
                  <TributacaoSpedValidacao />
                </ProtectedRoute>
              } />
              <Route path="tributacao/produtos" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfis']}>
                  <TributacaoProdutos />
                </ProtectedRoute>
              } />
              <Route path="tributacao/perfil-participante" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfis']}>
                  <TributacaoPerfilParticipante />
                </ProtectedRoute>
              } />
              <Route path="tributacao/perfil-origem-destino" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfis']}>
                  <TributacaoPerfilOrigemDestino />
                </ProtectedRoute>
              } />
              <Route path="tributacao/codigo-prestacao-servico" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfis']}>
                  <TributacaoCodigoPrestacaoServico />
                </ProtectedRoute>
              } />
              <Route path="tributacao/st" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfis']}>
                  <TributacaoCadastroSt />
                </ProtectedRoute>
              } />
              <Route path="tributacao/fecp-icms" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfis']}>
                  <TributacaoCadastroFecpIcms />
                </ProtectedRoute>
              } />
              <Route path="tributacao/fcp-st" element={
                <ProtectedRoute requiredPermission={['Tributação - Perfis']}>
                  <TributacaoCadastroFcpSt />
                </ProtectedRoute>
              } />
              <Route path="tributacao/ex-tarifario" element={
                <ProtectedRoute requiredPermission={['Tributação - NCM ICMS']}>
                  <TributacaoExTarifario />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-custo" element={
                <ProtectedRoute requiredPermission={['Tributação']}>
                  <TributacaoRegraCusto />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-calculo-doc-fiscal/perfil-operacao" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Cálculo', 'Tributação - Perfis']}>
                  <TributacaoTipoOperacao />
                </ProtectedRoute>
              } />
              <Route path="tributacao/tipo-operacao" element={<Navigate to="/tributacao/regra-calculo-doc-fiscal/perfil-operacao" replace />} />
              <Route path="tributacao/regra-base-calculo" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Cálculo']}>
                  <TributacaoRegraBaseCalculo />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-aliquota" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Cálculo']}>
                  <TributacaoRegraAliquota />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-documento-fiscal" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Cálculo']}>
                  <TributacaoRegraDocumentoFiscal />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-titulo" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo']}>
                  <TributacaoRegraTitulo />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-calculo-financeiro" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo']}>
                  <TributacaoRegraCalculoFinanceiro />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-vencimento" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo']}>
                  <TributacaoRegraVencimento />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-retencao" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo']}>
                  <TributacaoRegraRetencao />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-financeira" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Financeiras', 'Tributação - Regras Cálculo']}>
                  <TributacaoRegraFinanceira />
                </ProtectedRoute>
              } />
              <Route path="tributacao/regra-escrituracao" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Cálculo']}>
                  <TributacaoRegraEscrituracao />
                </ProtectedRoute>
              } />
              <Route path="tributacao/financial-rules" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Financeiras']}>
                  <TributacaoFinancialRulesPage />
                </ProtectedRoute>
              } />
              <Route path="tributacao/financial-rules/:slug" element={
                <ProtectedRoute requiredPermission={['Tributação - Regras Financeiras']}>
                  <TributacaoFinancialRulesPage />
                </ProtectedRoute>
              } />
              <Route path="tributacao/passo-a-passo" element={
                <ProtectedRoute requiredPermission={['Tributação - Confronto Fiscal', 'Tributação - Perfil Fiscal', 'Tributação - SPED', 'Tributação - Tokens API', 'Tributação']}>
                  <PassoAPasso />
                </ProtectedRoute>
              } />
              <Route path="tributacao/painel" element={
                <ProtectedRoute requiredPermission={['Tributação - Confronto Fiscal', 'Tributação - Perfil Fiscal', 'Tributação - SPED', 'Tributação - Tokens API', 'Tributação']}>
                  <PainelFiscal />
                </ProtectedRoute>
              } />
              <Route path="tributacao/confronto" element={
                <ProtectedRoute requiredPermission={['Tributação - Confronto Fiscal']}>
                  <TributacaoConfronto />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/fontes" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbFontes />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/novidades" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbNovidades />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cfop" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCfop />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/natureza-operacao" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbNatureza />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cst" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCst />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cclasstrib" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCclassTrib />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/localidades" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbLocalidades />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/ncm" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbNcm />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/dominios-nfe" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbDominios />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/tributos" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbTributos />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/regras" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbRegras />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/aliquotas" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbAliquotas />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/fcp" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbFcp />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/icms-st" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbIcmsSt />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cest" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCest />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cnae" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCnae />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/tipi" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbTipi />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cruzamento-ncm" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCruzamentoNcm />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/ex-tarifario" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbExTarifario />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/anp" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbAnp />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/utrib" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbUtrib />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/ptax" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbPtax />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/nbs" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbNbs />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/servicos-nfse" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbServicosNfse />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cindop" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCindOp />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/sped" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbSped />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/uf-beneficios" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbUfBeneficios />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/regimes-especiais" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbRegimesEspeciais />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/normas" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbNormas />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/convenios" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbConvenios />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/simples-nacional" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbSimples />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/cbenef" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbCbenef />
                </ProtectedRoute>
              } />
              <Route path="tributacao/base-conhecimento/iss-municipio" element={
                <ProtectedRoute requiredPermission={['Tributação - Base de Conhecimento']}>
                  <TributacaoKbIssMunicipio />
                </ProtectedRoute>
              } />
              <Route path="rh/recrutamento/funcoes" element={<ProtectedRoute requiredPermission="RH - Funções"><RhFuncoes /></ProtectedRoute>} />
              <Route path="rh/recrutamento/vagas" element={<ProtectedRoute requiredPermission="RH - Vagas"><RhVagas /></ProtectedRoute>} />
              <Route path="rh/recrutamento/candidatos" element={<RhCandidatos />} />
              <Route path="rh/recrutamento/candidaturas" element={<RhCandidaturas />} />
              <Route path="rh/recrutamento/entrevistas" element={<RhEntrevistas />} />
              <Route path="rh/recrutamento/pareceres" element={<RhPareceres />} />
              <Route path="rh/recrutamento/pipeline" element={<ProtectedRoute requiredPermission="RH - Pipeline"><RhPipeline /></ProtectedRoute>} />
              <Route path="operacao/to-do" element={
                <ProtectedRoute requiredPermission={['Operação - TO DO', 'Operação']}>
                  <OperacaoToDoKanban boardKey="palloma" title="TO DO - Palloma" />
                </ProtectedRoute>
              } />
              <Route path="operacao/to-do-bianca" element={
                <ProtectedRoute requiredPermission={['Operação - TO DO', 'Operação']}>
                  <OperacaoToDoKanban boardKey="bianca" title="TO DO - Bianca" />
                </ProtectedRoute>
              } />
              <Route path="operacao/fechamento" element={
                <ProtectedRoute requiredPermission="Operação - Fechamento">
                  <OperacaoFechamento />
                </ProtectedRoute>
              } />
              <Route path="layout" element={
                <ProtectedRoute requiredPermission="Layout">
                  <LayoutPage />
                </ProtectedRoute>
              } />
              <Route path="analises" element={
                <ProtectedRoute requiredPermission="Análises">
                  <Analises />
                </ProtectedRoute>
              } />
              <Route path="analises/resumo" element={
                <ProtectedRoute requiredPermission="Análises">
                  <AnaliseResumo />
                </ProtectedRoute>
              } />
              <Route path="geral" element={
                <ProtectedRoute requiredPermission="Análises">
                  <Geral />
                </ProtectedRoute>
              } />
              <Route path="analise-por-hora" element={
                <ProtectedRoute requiredPermission="Análises">
                  <AnalisePorHora />
                </ProtectedRoute>
              } />
              <Route path="consolidado" element={
                <ProtectedRoute requiredPermission="Análises">
                  <Consolidado />
                </ProtectedRoute>
              } />
              <Route path="folha-pagamento" element={
                <ProtectedRoute requiredPermission="Análises">
                  <FolhaPagamento />
                </ProtectedRoute>
              } />
              <Route path="rentabilidade-projetos" element={
                <ProtectedRoute requiredPermission={['Análises - Rentabilidade Projetos', 'Análises']}>
                  <RentabilidadeProjetos />
                </ProtectedRoute>
              } />
              <Route path="dashboards" element={<PaginaInicial />} />
              <Route path="configuracoes" element={<Configuracoes />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
